import { ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToElement } from "@/utils/scrollUtils";

export interface PhaseHeroProps {
  number: string;
  eyebrow: string;
  titleLead: string;
  titleHighlight: string;
  body: string;
  whatYouGet: string;
  whoItsFor: string;
  ctaLabel: string;
  image: string;
  reverse?: boolean;
}


const PhaseHero = ({
  number,
  eyebrow,
  titleLead,
  titleHighlight,
  body,
  whatYouGet,
  whoItsFor,
  ctaLabel,
  image,
}: PhaseHeroProps) => {

  return (
    <section className="relative overflow-hidden bg-background py-20 lg:py-24 border-t border-border/40">
      {/* Background layers (match ServiceHero) */}
      <div className="absolute inset-0 bg-hero-glow opacity-70" />
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.3] bg-[length:30px_30px]" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-gradient-to-br from-primary/5 to-accent/5 rounded-full filter blur-[120px] opacity-30" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-primary/5 to-accent/5 rounded-full filter blur-[100px] opacity-20" />

      <div className="container px-4 sm:px-6 lg:px-8 mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left — copy */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-background text-foreground text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="font-medium">
                Phase {number} — {eyebrow}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              {titleLead}{" "}
              <span className="text-primary">{titleHighlight}</span>
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              {body}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">
                  What you get
                </p>
                <p className="text-sm text-foreground/80 leading-relaxed">{whatYouGet}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">
                  Who it's for
                </p>
                <p className="text-sm text-foreground/80 leading-relaxed">{whoItsFor}</p>
              </div>
            </div>


            <div className="flex flex-wrap gap-4 pt-2">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => scrollToElement("content")}
              >
                {ctaLabel}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollToElement("content")}>
                Learn More
              </Button>
            </div>
          </div>

          {/* Right — Service Preview window */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="w-full max-w-md rounded-xl border border-border/50 bg-card shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-destructive/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-white/70" />
                </div>
                <span className="text-sm font-medium">Service Preview</span>
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="p-6 bg-background flex items-center justify-center">
                <img
                  src={image}
                  alt={`${titleLead} ${titleHighlight} — preview of the ${eyebrow} service phase`}
                  loading="lazy"
                  className="max-h-[240px] max-w-[320px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhaseHero;